import React, { useEffect, useState } from "react";
import { Container, Table, Spinner, Button, Pagination } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Utils } from "../../common/Utils";
import { useGlobal } from "../../contexts/GlobalContext";
import { FireStoreOrder } from "../../model/OrderModel";
import { OrderGateway } from "../../gateways/OrderGateway";

const UserOrders: React.FC = () => {
    const [orders, setOrders] = useState<FireStoreOrder[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const ordersPerPage = 12; // Number of orders per page
    const {settings, user} = useGlobal();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchOrders = async () => {
            if (!user) {
                console.log("No user is signed in.");
                setLoading(false);
                return;
            }

            const orderList = await OrderGateway.GetOrders(user.uid);
            orderList.sort((a, b) => new Date(b.order.createdAt).getTime() - new Date(a.order.createdAt).getTime());

            setOrders(orderList);
            setLoading(false);
        };

        fetchOrders();
    }, [user]);

    // Pagination logic
    const indexOfLastOrder = currentPage * ordersPerPage;
    const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;

    const currentOrders = orders.slice(indexOfFirstOrder, indexOfLastOrder);
    const totalPages = Math.ceil(orders.length / ordersPerPage);

    return (
        <Container className="main-container mt-4">
            <h2>Order History</h2>

            {loading || !settings ? (
                <Spinner animation="border" />
            ) : orders.length === 0 ? (
                <p className="text-center">You have no orders.</p>
            ) : (
                <>
                    <Table striped bordered hover responsive>
                        {settings.hasPrice ? 
                        (<colgroup>
                            <col style={{ width: "15%" }} />
                            <col style={{ width: "20%" }} />{/* Order summary column */}
                            <col style={{ width: "15%" }} />{/* Date column */}
                            <col style={{ width: "10%" }} />{/* Total Amount column */}
                            <col style={{ width: "10%" }} />{/* Status column */}
                            <col style={{ width: "10%" }} />{/* Delivery column */}
                            <col style={{ width: "20%" }} />{/* Action column */}
                        </colgroup>) : 
                        (<colgroup>
                            <col style={{ width: "15%" }} />
                            <col style={{ width: "30%" }} />{/* Order summary column */}
                            <col style={{ width: "15%" }} />{/* Date column */}
                            <col style={{ width: "10%" }} />{/* Status column */}
                            <col style={{ width: "10%" }} />{/* Delivery column */}
                            <col style={{ width: "20%" }} />{/* Action column */}
                        </colgroup>)}
                        <thead className="table-dark">
                            <tr>
                                <th>Order ID</th>
                                <th>Order Summary</th>
                                <th>Date</th>
                                {settings.hasPrice && <th>Total Amount</th>}
                                <th>Status</th>
                                <th>Delivery location</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentOrders.map(fireStoreOrder => (
                                <tr key={fireStoreOrder.id}>
                                    <td>{fireStoreOrder.order.orderNumber}</td>
                                    <td>
                                        {fireStoreOrder.order.cartItems.length > 1
                                            ? `${fireStoreOrder.order.cartItems[0].name} x ${fireStoreOrder.order.cartItems[0].quantity}, ${fireStoreOrder.order.cartItems[1].name}  x ${fireStoreOrder.order.cartItems[0].quantity} ...`
                                            : fireStoreOrder.order.cartItems.length > 0 ? `${fireStoreOrder.order.cartItems[0].name} x ${fireStoreOrder.order.cartItems[0].quantity}`
                                                : "No items"
                                        }
                                    </td>
                                    <td>{new Date(fireStoreOrder.order.createdAt).toLocaleString()}</td>
                                    {settings.hasPrice && <td className="text-end align-middle">{Utils.FormatCurrency(Number(fireStoreOrder.order.totalAmount), settings.currency)}</td>}
                                    <td>
                                        <span className={`badge ${fireStoreOrder.order.status === "Completed" ? "bg-success" : fireStoreOrder.order.status === "Cancelled" ? "bg-danger" : "bg-warning text-dark"}`}>
                                            {fireStoreOrder.order.status}
                                        </span>
                                    </td>
                                    <td>
                                        {fireStoreOrder.order.deliveryLocation !== "" ? fireStoreOrder.order.deliveryLocation : "Collection"}
                                    </td>
                                    <td>
                                        <div className="d-flex gap-2">
                                            <Button variant="primary" onClick={() => navigate(Utils.GetUrl(`/order/${fireStoreOrder.id}`))}>
                                                View Details
                                            </Button>
                                            <Button variant="secondary" onClick={() => navigate(Utils.GetUrl(`/cart/${fireStoreOrder.id}`))}>
                                                Repeat Order
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    {/* Pagination Controls */}
                    <Pagination className="mt-4 justify-content-center">
                        <Pagination.Prev
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                        />
                        {Array.from({ length: totalPages }, (_, i) => (
                            <Pagination.Item
                                key={i + 1}
                                active={i + 1 === currentPage}
                                onClick={() => setCurrentPage(i + 1)}
                            >
                                {i + 1}
                            </Pagination.Item>
                        ))}
                        <Pagination.Next
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                        />
                    </Pagination>
                </>
            )}
        </Container>
    );
};

export default UserOrders;